(() => {
  if (window.__complimentarySamplesAtomicInitialized) return;
  window.__complimentarySamplesAtomicInitialized = true;

  const maximumComplimentarySamples = 3;
  const eligibleProductTag = 'complimentary-samples-eligible';

  const complimentarySampleVariantIds = new Set([
    55523789635910,
    55523790750022,
    55523792126278,
    55523793142086,
    55523794256198,
    55523796615494,
    55523798942022,
    56272631693638,
  ]);

  const productEligibilityCache = new Map();

  let complimentarySampleCleanupRunning = false;
  let complimentarySampleCleanupTimer = null;

  /*
   * Return the localized Shopify root.
   * Examples: "/" or "/fr/".
   */
  const getShopifyRoot = () =>
    window.Shopify?.routes?.root || '/';

  /*
   * Retrieve the latest authoritative cart state.
   */
  const fetchCart = async () => {
    const root = getShopifyRoot();

    const response = await fetch(`${root}cart.js`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error('Unable to retrieve the current cart.');
    }

    return response.json();
  };

  /*
   * Identify complimentary-sample variants.
   */
  const isComplimentarySampleItem = (item) => {
    const variantId = Number(
      item?.variant_id || item?.id
    );

    return complimentarySampleVariantIds.has(variantId);
  };

  /*
   * Update the cart bubble after AJAX cart changes.
   */
  const updateCartBubble = (itemCount) => {
    document
      .querySelectorAll('#cart-icon-bubble')
      .forEach((cartIcon) => {
        let bubble = cartIcon.querySelector(
          '.cart-count-bubble'
        );

        if (itemCount <= 0) {
          bubble?.remove();
          return;
        }

        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = `
            <span aria-hidden="true"></span>
            <span class="visually-hidden"></span>
          `;

          cartIcon.appendChild(bubble);
        }

        const visibleCount = bubble.querySelector(
          'span[aria-hidden="true"]'
        );

        const accessibleCount = bubble.querySelector(
          '.visually-hidden'
        );

        if (visibleCount) {
          visibleCount.textContent = String(itemCount);
        }

        if (accessibleCount) {
          accessibleCount.textContent =
            itemCount === 1
              ? '1 item in cart'
              : `${itemCount} items in cart`;
        }
      });
  };

  /*
   * Preserve the native Taste cart notification.
   */
  const showCartNotification = ({
    responseData,
    mainVariantId,
  }) => {
    const notification = document.querySelector(
      'cart-notification'
    );

    if (
      !notification ||
      typeof notification.renderContents !== 'function'
    ) {
      return;
    }

    try {
      const addedItems = Array.isArray(responseData.items)
        ? responseData.items
        : [];

      const mainAddedItem =
        addedItems.find(
          (item) =>
            Number(item.variant_id) ===
            Number(mainVariantId)
        ) ||
        addedItems.find(
          (item) =>
            Number(item.id) === Number(mainVariantId)
        ) ||
        addedItems[0];

      if (mainAddedItem?.key) {
        responseData.key = mainAddedItem.key;
      }

      if (
        !responseData.sections ||
        typeof responseData.sections !== 'object'
      ) {
        console.warn(
          'Cart notification skipped: no rendered sections were returned.'
        );

        return;
      }

      notification.renderContents(responseData);
    } catch (notificationError) {
      console.warn(
        'Cart notification could not be rendered:',
        notificationError
      );
    }
  };

  /*
   * Read a hidden or visible line-item property from Shopify
   * cart JSON. Shopify may return properties as an object or
   * as an array of key/value pairs.
   */
  const getLineItemProperty = (
    item,
    propertyName
  ) => {
    const properties = item?.properties;

    if (!properties) return null;

    if (Array.isArray(properties)) {
      const matchingProperty = properties.find(
        (property) =>
          Array.isArray(property) &&
          property[0] === propertyName
      );

      return matchingProperty?.[1] ?? null;
    }

    return properties[propertyName] ?? null;
  };

  /*
   * New qualifying bottles added through this script receive
   * a hidden line-item property.
   */
  const lineItemHasEligibilityProperty = (item) => {
    const value = getLineItemProperty(
      item,
      '_complimentary_samples_eligible'
    );

    return (
      value === true ||
      value === 1 ||
      String(value).toLowerCase() === 'true'
    );
  };

  /*
   * Resolve a product handle from Shopify cart JSON.
   */
  const getProductHandle = (item) => {
    if (item?.handle) {
      return String(item.handle);
    }

    const itemUrl = String(item?.url || '');

    const match = itemUrl.match(
      /\/products\/([^/?#]+)/
    );

    return match?.[1] || '';
  };

  /*
   * Check the actual Shopify product tag. This is the fallback
   * for qualifying bottles that were added before the hidden
   * line-item property was introduced, or added without selecting
   * samples on the product page.
   */
  const productHandleHasEligibilityTag = async (
    handle
  ) => {
    if (!handle) return false;

    if (!productEligibilityCache.has(handle)) {
      const root = getShopifyRoot();

      const request = fetch(
        `${root}products/${encodeURIComponent(
          handle
        )}.js`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          cache: 'no-store',
        }
      )
        .then(async (response) => {
          if (!response.ok) return false;

          const product = await response.json();

          const tags = Array.isArray(product.tags)
            ? product.tags
            : String(product.tags || '')
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean);

          return tags.includes(eligibleProductTag);
        })
        .catch((error) => {
          console.warn(
            'Unable to verify complimentary-sample eligibility:',
            error
          );

          return false;
        });

      productEligibilityCache.set(handle, request);
    }

    return productEligibilityCache.get(handle);
  };

  /*
   * Determine whether a cart item is a qualifying full-size
   * fragrance.
   */
  const lineItemIsEligibleProduct = async (item) => {
    if (isComplimentarySampleItem(item)) {
      return false;
    }

    if (lineItemHasEligibilityProperty(item)) {
      return true;
    }

    const handle = getProductHandle(item);

    return productHandleHasEligibilityTag(handle);
  };

  /*
   * Disable checkout only while orphaned samples are actively
   * being removed. The data attribute ensures that we do not
   * accidentally re-enable a button disabled by Shopify for
   * another reason.
   */
  const setCleanupCheckoutState = (disabled) => {
    document
      .querySelectorAll(
        '#checkout, #CartDrawer-Checkout, [name="checkout"]'
      )
      .forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) {
          return;
        }

        if (disabled) {
          if (!button.disabled) {
            button.dataset.disabledBySampleCleanup =
              'true';
          }

          button.disabled = true;
          button.setAttribute(
            'aria-disabled',
            'true'
          );

          return;
        }

        if (
          button.dataset.disabledBySampleCleanup ===
          'true'
        ) {
          button.disabled = false;
          button.removeAttribute('aria-disabled');

          delete button.dataset
            .disabledBySampleCleanup;
        }
      });
  };

  /*
   * Remove all complimentary-sample line items when no
   * qualifying full-size fragrance remains in the cart.
   */
  const removeOrphanedComplimentarySamples =
    async () => {
      if (complimentarySampleCleanupRunning) {
        return;
      }

      complimentarySampleCleanupRunning = true;

      try {
        const cart = await fetchCart();

        const complimentarySampleItems =
          cart.items.filter(
            isComplimentarySampleItem
          );

        /*
         * No samples means there is nothing to validate.
         */
        if (!complimentarySampleItems.length) {
          setCleanupCheckoutState(false);
          return;
        }

        const possibleEligibleItems =
          cart.items.filter(
            (item) =>
              !isComplimentarySampleItem(item)
          );

        const eligibilityResults =
          await Promise.all(
            possibleEligibleItems.map((item) =>
              lineItemIsEligibleProduct(item)
            )
          );

        const hasEligibleProduct =
          eligibilityResults.some(Boolean);

        /*
         * Keep the samples when at least one qualifying bottle
         * remains anywhere in the cart.
         */
        if (hasEligibleProduct) {
          setCleanupCheckoutState(false);
          return;
        }

        setCleanupCheckoutState(true);

        const updates = {};

        complimentarySampleItems.forEach((item) => {
          /*
           * Line-item keys are safer than variant IDs because
           * Shopify can contain separate lines for the same
           * variant when properties differ.
           */
          updates[item.key] = 0;
        });

        const root = getShopifyRoot();

        const response = await fetch(
          window.routes?.cart_update_url ||
            `${root}cart/update.js`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ updates }),
          }
        );

        const updatedCart = await response.json();

        if (!response.ok || updatedCart.status) {
          throw new Error(
            updatedCart.description ||
              updatedCart.message ||
              'Unable to remove complimentary samples.'
          );
        }

        updateCartBubble(
          Number(updatedCart.item_count || 0)
        );

        /*
         * Rebuild the cart page or drawer from Shopify's actual
         * cart state. On the following load no samples remain,
         * so this cannot create a reload loop.
         */
        window.location.reload();
      } catch (error) {
        console.error(
          'Complimentary-sample cart validation error:',
          error
        );

        /*
         * Do not leave checkout permanently disabled after an
         * unexpected network error.
         */
        setCleanupCheckoutState(false);
      } finally {
        complimentarySampleCleanupRunning = false;
      }
    };

  /*
   * Debounce checks because Taste may fire several DOM and
   * cart events during a single quantity update.
   */
  const scheduleComplimentarySampleCleanup = (
    delay = 300
  ) => {
    window.clearTimeout(
      complimentarySampleCleanupTimer
    );

    complimentarySampleCleanupTimer =
      window.setTimeout(
        removeOrphanedComplimentarySamples,
        delay
      );
  };

  /*
   * Submit the qualifying bottle and selected samples in one
   * atomic Shopify cart request.
   */
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const productForm = form.closest(
        'product-form'
      );

      if (!productForm) return;

      const selects = Array.from(
        productForm.querySelectorAll(
          '.complimentary-sample-select'
        )
      );

      /*
       * Products without the complimentary-sample selectors
       * continue through Taste's normal product-form flow.
       */
      if (!selects.length) return;

      const sampleIds = [
        ...new Set(
          selects
            .map((select) =>
              String(select.value).trim()
            )
            .filter(Boolean)
        ),
      ];

      /*
       * No selected samples means Taste may submit the product
       * normally.
       */
      if (!sampleIds.length) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const submitButton =
        productForm.querySelector(
          'button[type="submit"][name="add"]'
        );

      const spinner = submitButton?.querySelector(
        '.loading__spinner'
      );

      submitButton?.setAttribute(
        'aria-disabled',
        'true'
      );

      submitButton?.classList.add('loading');
      spinner?.classList.remove('hidden');

      try {
        const formData = new FormData(form);

        const mainVariantId = Number(
          formData.get('id')
        );

        const quantity = Math.max(
          1,
          Number(formData.get('quantity') || 1)
        );

        if (!mainVariantId) {
          throw new Error(
            'The selected product variant is unavailable.'
          );
        }

        /*
         * Mark the qualifying bottle with a hidden property.
         * Properties beginning with "_" are already hidden by
         * the theme's cart templates.
         */
        const properties = {
          _complimentary_samples_eligible: 'true',
        };

        for (const [key, value] of formData.entries()) {
          const propertyMatch = key.match(
            /^properties\[(.+)\]$/
          );

          if (
            propertyMatch &&
            String(value).trim()
          ) {
            properties[propertyMatch[1]] = value;
          }
        }

        const mainItem = {
          id: mainVariantId,
          quantity,
        };

        if (Object.keys(properties).length) {
          mainItem.properties = properties;
        }

        const sellingPlan =
          formData.get('selling_plan');

        if (sellingPlan) {
          mainItem.selling_plan =
            Number(sellingPlan);
        }

        const existingCart = await fetchCart();

        const existingSampleVariantIds =
          new Set();

        const existingSampleCount =
          existingCart.items.reduce(
            (total, item) => {
              if (
                !isComplimentarySampleItem(item)
              ) {
                return total;
              }

              const variantId = Number(
                item.variant_id || item.id
              );

              existingSampleVariantIds.add(
                variantId
              );

              return (
                total +
                Number(item.quantity || 0)
              );
            },
            0
          );

        const availableSampleSlots = Math.max(
          0,
          maximumComplimentarySamples -
            existingSampleCount
        );

        const samplesToAdd = sampleIds
          .map(Number)
          .filter(
            (sampleId) =>
              complimentarySampleVariantIds.has(
                sampleId
              ) &&
              !existingSampleVariantIds.has(
                sampleId
              )
          )
          .slice(0, availableSampleSlots);

        const items = [
          mainItem,
          ...samplesToAdd.map((sampleId) => ({
            id: sampleId,
            quantity: 1,
          })),
        ];

        const notification =
          document.querySelector(
            'cart-notification'
          );

        let sectionIds = [];

        if (
          notification &&
          typeof notification.getSectionsToRender ===
            'function'
        ) {
          sectionIds = notification
            .getSectionsToRender()
            .map((section) => section.id)
            .filter(Boolean);
        }

        if (
          !sectionIds.includes(
            'cart-icon-bubble'
          )
        ) {
          sectionIds.push('cart-icon-bubble');
        }

        sectionIds = [...new Set(sectionIds)];

        const requestBody = {
          items,
          sections: sectionIds,
          sections_url:
            window.location.pathname +
            window.location.search,
        };

        const response = await fetch(
          window.routes?.cart_add_url ||
            '/cart/add.js',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With':
                'XMLHttpRequest',
            },
            body: JSON.stringify(requestBody),
          }
        );

        const responseData =
          await response.json();

        if (!response.ok || responseData.status) {
          throw new Error(
            responseData.description ||
              responseData.message ||
              'Unable to add the selected product.'
          );
        }

        showCartNotification({
          responseData,
          mainVariantId,
        });

        const updatedCart = await fetchCart();

        updateCartBubble(
          Number(updatedCart.item_count || 0)
        );
      } catch (error) {
        console.error(
          'Complimentary samples error:',
          error
        );

        if (
          typeof productForm.handleErrorMessage ===
          'function'
        ) {
          productForm.handleErrorMessage(
            error.message
          );
        } else {
          const errorWrapper =
            productForm.querySelector(
              '.product-form__error-message-wrapper'
            );

          const errorMessage =
            productForm.querySelector(
              '.product-form__error-message'
            );

          if (errorWrapper && errorMessage) {
            errorMessage.textContent =
              error.message;

            errorWrapper.removeAttribute(
              'hidden'
            );
          }
        }
      } finally {
        submitButton?.classList.remove(
          'loading'
        );

        submitButton?.removeAttribute(
          'aria-disabled'
        );

        spinner?.classList.add('hidden');
      }
    },
    true
  );

  /*
   * Taste normally publishes a cartUpdate event after line-item
   * removal and quantity changes.
   */
  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS?.cartUpdate
  ) {
    subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      () => {
        scheduleComplimentarySampleCleanup(
          350
        );
      }
    );
  }

  /*
   * Fallback for cart controls in theme versions or situations
   * where the standard cartUpdate event is unavailable.
   *
   * Multiple delayed checks cover slower mobile connections.
   */
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const cartControl = event.target.closest(
        [
          'cart-remove-button',
          '.cart-remove-button',
          '.quantity__button',
        ].join(',')
      );

      if (!cartControl) return;

      scheduleComplimentarySampleCleanup(700);

      window.setTimeout(
        removeOrphanedComplimentarySamples,
        1500
      );
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      if (
        event.target.matches(
          '.quantity__input[name="updates[]"]'
        )
      ) {
        scheduleComplimentarySampleCleanup(
          700
        );

        window.setTimeout(
          removeOrphanedComplimentarySamples,
          1500
        );
      }
    },
    true
  );

  /*
   * Validate carts restored through refresh, browser back/forward
   * cache, or direct navigation to /cart.
   */
  const initializeCartValidation = () => {
    scheduleComplimentarySampleCleanup(150);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeCartValidation,
      { once: true }
    );
  } else {
    initializeCartValidation();
  }

  window.addEventListener('pageshow', () => {
    scheduleComplimentarySampleCleanup(150);
  });
})();